import { FilterFilled } from "@ant-design/icons";
import { Button, notification, Table } from "antd";
import { ColumnType } from "antd/es/table";
import { useFormikContext } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useGetStandAloneConfigurationsQuery } from "shared/api";
import { StandAloneItem } from "shared/api/standAlone/models";
import {
  useDebouncedTableFilter,
  usePagination,
  useStandAloneRequests,
  useTableSorting,
} from "shared/hooks";
import { StandAloneFormik } from "shared/types";
import { TableButton, TableSearchFilter } from "shared/ui";

interface StandAloneTableProps {
  showEditStandAloneModal: () => void;
}

const StandAloneTable: FC<StandAloneTableProps> = ({
  showEditStandAloneModal,
}) => {
  const { t } = useTranslation("", { keyPrefix: "standAlone" });

  const [api, contextHolder] = notification.useNotification();

  const { offset, limit, pageSize, currentPage, setCurrentPage, setPageSize } =
    usePagination();

  const { ordering } = useTableSorting();

  const { setValues } = useFormikContext<StandAloneFormik>();

  const editStandAloneItem = (standAlone: StandAloneItem) => {
    showEditStandAloneModal();
    setValues(standAlone);
  };

  const {
    debouncedValue: debouncedNameValue,
    handleFilterChange: handleNameFilterChange,
    value: nameFilterValue,
    clearFilterValue: clearNameFilter,
  } = useDebouncedTableFilter("name", 400);

  const {
    debouncedValue: debouncedDescriptionValue,
    handleFilterChange: handleDescriptionFilterChange,
    value: descriptionFilterValue,
    clearFilterValue: clearDescriptionFilter,
  } = useDebouncedTableFilter("description", 400);

  const {
    debouncedValue: debouncedUrlValue,
    handleFilterChange: handleUrlFilterChange,
    value: urlFilterValue,
    clearFilterValue: clearUrlFilter,
  } = useDebouncedTableFilter("url", 400);

  const {
    data: standAloneData,
    isLoading,
    isFetching,
  } = useGetStandAloneConfigurationsQuery({
    offset,
    limit,
    ordering,
    name__icontains: debouncedNameValue || undefined,
    description__icontains: debouncedDescriptionValue || undefined,
    url__icontains: debouncedUrlValue || undefined,
  });

  const { showStandAloneDeleteModal } = useStandAloneRequests({ api });

  const columns: ColumnType<StandAloneItem>[] = [
    {
      title: t("name"),
      filterIcon: (
        <FilterFilled
          className={
            nameFilterValue.length ? "!text-blue-600  scale-125" : undefined
          }
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearNameFilter}
          confirm={confirm}
          value={nameFilterValue!}
          handleFilterChange={handleNameFilterChange!}
          visible={visible}
          placeholder={t("Enter the name")!}
          title={t("itemView.serialNumber")!}
        />
      ),
      render: (standAlone: StandAloneItem) => (
        <Button
          onClick={() => editStandAloneItem(standAlone)}
          className="border-none hover:border-none bg-transparent shadow-none"
        >
          <span className="underline">{standAlone?.name}</span>
        </Button>
      ),
    },
    {
      title: t("description"),
      filterIcon: (
        <FilterFilled
          className={
            descriptionFilterValue.length
              ? "!text-blue-600 scale-125"
              : undefined
          }
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearDescriptionFilter}
          confirm={confirm}
          value={descriptionFilterValue!}
          handleFilterChange={handleDescriptionFilterChange!}
          visible={visible}
          placeholder={t("Enter the description")!}
          title={t("Description")!}
        />
      ),
      render: (standAlone: StandAloneItem) => standAlone.description ?? "-",
    },
    {
      title: t("url"),
      filterIcon: (
        <FilterFilled
          className={
            urlFilterValue.length ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearUrlFilter}
          confirm={confirm}
          value={urlFilterValue!}
          handleFilterChange={handleUrlFilterChange!}
          visible={visible}
          placeholder={t("Enter the URL")!}
          title={t("URL")!}
        />
      ),
      render: (standAlone: StandAloneItem) => standAlone.url ?? "-",
    },
    {
      title: t("bucket"),
      render: (standAlone: StandAloneItem) => standAlone.bucket,
    },
    {
      title: t("org"),
      render: (standAlone: StandAloneItem) => standAlone.org,
    },
    {
      title: t("actions"),
      render: (standAlone: StandAloneItem) => {
        return (
          <TableButton
            type="delete"
            onClick={() => showStandAloneDeleteModal(standAlone)}
          />
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <Table
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={standAloneData?.results}
        rowKey={(obj) => obj.id!}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: standAloneData?.count,
        }}
      />
    </>
  );
};

export { StandAloneTable };
